/**
 * Disbursement Files Table Component
 */

import {
  FILE_STATUS,
  FILE_STATUS_DISPLAY,
  type FileStatusId,
  isInternalAdminUser,
} from '@steerfi/shared';
import { AlertCircle, Ban, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { Fragment, useCallback, useState } from 'react';
import { toast } from 'sonner';
import {
  getDisbursementById,
  ReleaseDisbursementError,
  releaseDisbursement,
} from '@/api/disbursements';
import { updatePaymentFileStatus } from '@/api/payments';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
} from '@/components/ui/table';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { cn, currencyFormatter, formatDate, formatDateTime } from '@/lib/utils';
import type {
  DisbursementFile,
  LastReleaseError,
  PaymentFileReference,
} from '@/types/disbursement';
import { ChannelChip, type PaymentStatus, StatusChip } from './status-chip';
import { Badge } from './ui/badge';

/**
 * UUID-style ids: strip dashes, then first 8 hex chars + **** + last 8 hex chars.
 * e.g. 3766c736-a24e-4c66-b48c-034b886f1a62 → 3766c736****886f1a62
 */
function maskSourceFileIdCompact(raw: string): string {
  const normalized = raw.replace(/-/g, '').replace(/[^a-fA-F0-9]/g, '');
  if (normalized.length >= 16) {
    return `${normalized.slice(0, 8)}****${normalized.slice(-8)}`;
  }
  if (raw.length <= 8) {
    return raw;
  }
  return `${raw.slice(0, 4)}****${raw.slice(-4)}`;
}

/** Strip "Generated disbursement for"; parse `source file <id>` or fall back to related file / disbursement id. */
function getMaskedSourceFileId(file: {
  id: string;
  reference?: string;
  relatedPaymentFiles: PaymentFileReference[];
}): string {
  const raw = (file.reference ?? '').replace(/^Generated disbursement for\s+/i, '').trim();
  const sourceFileMatch = raw.match(/^source file\s+(.+)$/i);
  let idToMask: string;
  if (sourceFileMatch) {
    idToMask = sourceFileMatch[1].trim();
  } else if (raw.length > 0) {
    idToMask = raw;
  } else if (file.relatedPaymentFiles[0]?.paymentFileId) {
    idToMask = file.relatedPaymentFiles[0].paymentFileId;
  } else {
    idToMask = file.id;
  }
  return maskSourceFileIdCompact(idToMask);
}

interface Props {
  disbursementFiles: DisbursementFile[];
  onRefresh?: () => void | Promise<void>;
  onDownload?: (file: DisbursementFile) => void | Promise<void>;
}

interface DetailState {
  loading: boolean;
  lastReleaseError?: LastReleaseError | null;
  fetchError?: string;
  /**
   * Set after a release attempt fails so the chip + panel can reflect the
   * failure even when the post-attempt server reads also fail (e.g. file-server
   * is down — both detail and list GETs proxy through it). Cleared as soon as a
   * fresh server read succeeds.
   */
  optimisticStatusTypeId?: FileStatusId;
  optimisticLastReleaseError?: LastReleaseError;
}

const LastReleaseErrorPanel = ({ error }: { error: LastReleaseError }) => {
  return (
    <div
      role="alert"
      data-testid="last-release-error-panel"
      className="rounded-md border border-amber-200 bg-amber-50/70 p-4 text-sm dark:border-amber-800 dark:bg-amber-900/20"
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="space-y-2">
          <div>
            <p className="font-semibold text-amber-900 dark:text-amber-200">
              Last release attempt failed
            </p>
            <p className="text-amber-900 dark:text-amber-100">{error.message}</p>
          </div>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs text-amber-900/80 dark:text-amber-200/80">
            <dt className="font-medium">Attempted at</dt>
            <dd>{formatDateTime(error.attemptedAt)}</dd>
            <dt className="font-medium">Attempted by</dt>
            <dd className="break-all">{error.attemptedBy}</dd>
          </dl>
        </div>
      </div>
    </div>
  );
};

export const DisbursementFilesTable = ({ disbursementFiles, onRefresh, onDownload }: Props) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [releasingIds, setReleasingIds] = useState<Set<string>>(new Set());
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set());
  const [cancelConfirmFileId, setCancelConfirmFileId] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, DetailState>>({});
  const { user } = useKeycloakAuth();
  // Releasing/cancelling a disbursement is money movement — an admin-tier action.
  const canManageDisbursements = isInternalAdminUser(user?.roles);

  const fetchDetail = useCallback(async (fileId: string) => {
    setDetails((prev) => ({
      ...prev,
      [fileId]: { ...(prev[fileId] ?? { loading: false }), loading: true },
    }));
    try {
      const detail = await getDisbursementById(fileId);
      // Success replaces the entire entry — optimistic overrides are dropped in
      // favor of authoritative server state.
      setDetails((prev) => ({
        ...prev,
        [fileId]: { loading: false, lastReleaseError: detail.lastReleaseError ?? null },
      }));
    } catch (error) {
      // Preserve any optimistic overrides on failure (file-server may be down,
      // which is the case the optimistic update exists to cover).
      setDetails((prev) => ({
        ...prev,
        [fileId]: {
          ...(prev[fileId] ?? { loading: false }),
          loading: false,
          fetchError: error instanceof Error ? error.message : 'Failed to load details',
        },
      }));
    }
  }, []);

  const pendingCancelFile = cancelConfirmFileId
    ? disbursementFiles.find((f) => f.id === cancelConfirmFileId)
    : undefined;

  const runCancelDisbursement = async (fileId: string) => {
    setCancelConfirmFileId(null);
    setCancellingIds((prev) => new Set(prev).add(fileId));
    try {
      await updatePaymentFileStatus(fileId, FILE_STATUS.CANCELLED);
      toast.warning('Disbursement cancelled', {
        description: 'The disbursement file has been cancelled.',
      });
      if (onRefresh) await onRefresh();
    } catch (error) {
      toast.error('Failed to cancel disbursement', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setCancellingIds((prev) => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
        if (!details[id]) {
          void fetchDetail(id);
        }
      }
      return newSet;
    });
  };

  if (disbursementFiles.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            <p>No disbursement files found</p>
            <p className="text-sm mt-2">
              Disbursement files will appear here once payment files are processed and funds are
              distributed to suppliers.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="p-0">
          <Table caption="Disbursement files">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <span className="sr-only">Expand</span>
                </TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Payments</TableHead>
                <TableHead className="text-right">Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {disbursementFiles.map((file) => {
                const detail = details[file.id];
                const effectiveStatusTypeId = detail?.optimisticStatusTypeId ?? file.status_type_id;
                const isReleased =
                  effectiveStatusTypeId != null
                    ? effectiveStatusTypeId === FILE_STATUS.RELEASED
                    : file.status === FILE_STATUS_DISPLAY[FILE_STATUS.RELEASED];
                const isCancelled =
                  effectiveStatusTypeId != null
                    ? effectiveStatusTypeId === FILE_STATUS.CANCELLED
                    : file.status === FILE_STATUS_DISPLAY[FILE_STATUS.CANCELLED];
                const isReleaseFailed =
                  effectiveStatusTypeId != null
                    ? effectiveStatusTypeId === FILE_STATUS.RELEASE_FAILED
                    : file.status === FILE_STATUS_DISPLAY[FILE_STATUS.RELEASE_FAILED];
                const panelError = detail?.optimisticLastReleaseError ?? detail?.lastReleaseError;
                return (
                  <Fragment key={file.id}>
                    <TableRow
                      key={file.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => toggleRow(file.id)}
                    >
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          aria-label={
                            expandedRows.has(file.id)
                              ? `Collapse disbursement ${file.reference || file.id}`
                              : `Expand disbursement ${file.reference || file.id}`
                          }
                          aria-expanded={expandedRows.has(file.id)}
                          onClick={(e) => {
                            // The row's onClick would toggle this anyway, but
                            // relying on the bubble leaves keyboard users at
                            // the mercy of whatever guard the row grows next.
                            e.stopPropagation();
                            toggleRow(file.id);
                          }}
                        >
                          {expandedRows.has(file.id) ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                      {/* The row header is the second cell, not the first: the
                          first is the expander control, which names nothing. An
                          explicit scope is what makes that legal. */}
                      <TableRowHeader className="font-medium">
                        {file.reference || file.id.slice(0, 8)}
                      </TableRowHeader>
                      <TableCell>
                        <Badge variant="outline" className={cn('text-xs font-medium')}>
                          {file.providerCode || '—'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(file.paymentMethodCodes.length > 0
                            ? file.paymentMethodCodes
                            : ['unknown']
                          ).map((code) => (
                            <ChannelChip key={code} channel={code} />
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          status={file.status as PaymentStatus}
                          type="payment"
                          {...(isReleaseFailed ? { variant: 'warning' as const } : {})}
                          {...(effectiveStatusTypeId != null
                            ? {
                                labelOverride:
                                  FILE_STATUS_DISPLAY[effectiveStatusTypeId as FileStatusId] ??
                                  file.status,
                              }
                            : file.status !== 'unknown'
                              ? { labelOverride: file.status }
                              : {})}
                        />
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {currencyFormatter.format(file.totalAmount)}
                      </TableCell>
                      <TableCell className="text-right">{file.disbursementCount}</TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatDate(file.createdAt)}
                      </TableCell>
                    </TableRow>
                    {expandedRows.has(file.id) && (
                      <TableRow key={`${file.id}-expanded`}>
                        <TableCell colSpan={8} className="bg-muted/30 p-4">
                          <div className="space-y-3">
                            {panelError ? <LastReleaseErrorPanel error={panelError} /> : null}
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-sm">Related Payment Files</h4>
                              <span className="text-sm text-muted-foreground">
                                {file.relatedPaymentFiles.length} payment file
                                {file.relatedPaymentFiles.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <div className="grid gap-2">
                              {file.relatedPaymentFiles.map((paymentFile) => (
                                <div
                                  key={paymentFile.paymentFileId}
                                  className="flex items-center justify-between bg-background p-3 rounded-md border"
                                >
                                  <div className="space-y-1">
                                    <p className="font-medium text-sm">
                                      {paymentFile.paymentReference}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      ID: {paymentFile.paymentFileId}
                                    </p>
                                  </div>
                                  <div className="text-right space-y-1">
                                    <p className="font-medium text-sm">
                                      {currencyFormatter.format(paymentFile.contributionAmount)}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {paymentFile.contributionPercentage.toFixed(1)}% of total
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="flex items-center justify-between pt-2 border-t">
                              {file.processedAt && (
                                <div className="text-sm text-muted-foreground">
                                  Processed: {formatDate(file.processedAt)}
                                </div>
                              )}
                              <div className="flex items-center gap-2">
                                {onDownload && (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDownload(file);
                                    }}
                                  >
                                    <Download className="mr-2 h-4 w-4" />
                                    Download File
                                  </Button>
                                )}
                                {canManageDisbursements && (
                                  <Button
                                    variant="default"
                                    size="sm"
                                    disabled={
                                      releasingIds.has(file.id) || isReleased || isCancelled
                                    }
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      setReleasingIds((prev) => new Set(prev).add(file.id));

                                      try {
                                        const result = await releaseDisbursement(file.id);
                                        toast.success('Disbursement released successfully', {
                                          description: result.data?.remotePath
                                            ? `Sent to: ${result.data.remotePath}`
                                            : undefined,
                                        });
                                        // Clear any prior optimistic-failed override so the panel
                                        // disappears immediately even if the post-release detail
                                        // fetch hasn't returned yet.
                                        setDetails((prev) => {
                                          const existing = prev[file.id];
                                          if (
                                            !existing?.optimisticStatusTypeId &&
                                            !existing?.optimisticLastReleaseError
                                          ) {
                                            return prev;
                                          }
                                          const {
                                            optimisticStatusTypeId: _omitStatus,
                                            optimisticLastReleaseError: _omitErr,
                                            ...rest
                                          } = existing;
                                          return { ...prev, [file.id]: rest };
                                        });
                                      } catch (error) {
                                        const message =
                                          error instanceof Error ? error.message : 'Unknown error';
                                        const description =
                                          error instanceof ReleaseDisbursementError &&
                                          error.errorCode
                                            ? `${message} (${error.errorCode})`
                                            : message;
                                        toast.error('Failed to release disbursement', {
                                          description,
                                        });

                                        // Optimistically reflect the failed attempt so the chip and
                                        // panel update even when the post-attempt server reads also
                                        // fail (e.g. file-server is down — both detail and list GETs
                                        // proxy through it). Backend writes RELEASE_FAILED on 422
                                        // (file-server) and on 502 unreachable (api-server), so once
                                        // reads recover the override is replaced with server truth.
                                        const releaseErr =
                                          error instanceof ReleaseDisbursementError ? error : null;
                                        if (
                                          releaseErr &&
                                          (releaseErr.isUnreachable ||
                                            releaseErr.status === 422 ||
                                            releaseErr.status === 502)
                                        ) {
                                          const optimisticError: LastReleaseError = {
                                            message,
                                            attemptedAt: new Date().toISOString(),
                                            attemptedBy: user?.email || user?.name || 'unknown',
                                          };
                                          setDetails((prev) => ({
                                            ...prev,
                                            [file.id]: {
                                              ...(prev[file.id] ?? { loading: false }),
                                              optimisticStatusTypeId: FILE_STATUS.RELEASE_FAILED,
                                              optimisticLastReleaseError: optimisticError,
                                            },
                                          }));
                                        }
                                      } finally {
                                        setReleasingIds((prev) => {
                                          const newSet = new Set(prev);
                                          newSet.delete(file.id);
                                          return newSet;
                                        });
                                        // Refetch detail (success → cleared panel, failure → new lastReleaseError)
                                        // before refreshing the list so chip/state stay consistent.
                                        await fetchDetail(file.id);
                                        if (onRefresh) await onRefresh();
                                      }
                                    }}
                                  >
                                    {isCancelled
                                      ? 'Disbursement cancelled'
                                      : isReleased
                                        ? 'Already Released'
                                        : releasingIds.has(file.id)
                                          ? 'Releasing...'
                                          : isReleaseFailed
                                            ? 'Retry Release'
                                            : 'Release Disbursement'}
                                  </Button>
                                )}
                                {canManageDisbursements && (
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    disabled={
                                      releasingIds.has(file.id) ||
                                      cancellingIds.has(file.id) ||
                                      isReleased ||
                                      isCancelled
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCancelConfirmFileId(file.id);
                                    }}
                                  >
                                    <Ban className="mr-2 h-4 w-4" />
                                    {isCancelled
                                      ? 'Disbursement cancelled'
                                      : cancellingIds.has(file.id)
                                        ? 'Cancelling...'
                                        : 'Cancel Disbursement'}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AlertDialog
        open={cancelConfirmFileId !== null}
        onOpenChange={(open) => {
          if (!open) setCancelConfirmFileId(null);
        }}
      >
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader className="space-y-0">
            <AlertDialogTitle className="leading-snug">Cancel this disbursement?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="mt-4 space-y-3 text-left">
                <p className="text-muted-foreground text-sm font-normal leading-relaxed">
                  This will cancel the disbursement permanently.
                </p>
                {pendingCancelFile ? (
                  <p className="text-sm leading-relaxed">
                    <span className="font-semibold text-foreground">Source file</span>
                    <span className="text-muted-foreground"> : </span>
                    <span className="font-mono text-xs tabular-nums text-muted-foreground">
                      {getMaskedSourceFileId(pendingCancelFile)}
                    </span>
                  </p>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel type="button">Keep disbursement</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              disabled={cancelConfirmFileId != null && cancellingIds.has(cancelConfirmFileId)}
              onClick={(e) => {
                e.stopPropagation();
                if (cancelConfirmFileId) void runCancelDisbursement(cancelConfirmFileId);
              }}
            >
              {cancelConfirmFileId != null && cancellingIds.has(cancelConfirmFileId)
                ? 'Cancelling…'
                : 'Yes, cancel disbursement'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
